import { Injectable, OnDestroy } from '@angular/core';
import { App } from '@df/core/app';
import { MultisiteService } from '@df/core/service/multisite';
import { AbstractDfModalInitiatorService } from '@df/ui/modal/abstract-df-modal-initiator.service';
import { ICmsModalService } from '@icms/client/assets/modal/icms-modal.service';
import { ICmsBlock, NICmsBlock } from '@icms/core/resource/block/icms-block';
import { ICmsBlockResource } from '@icms/core/resource/block/icms-block.resource';
import { Subscription } from 'rxjs/internal/Subscription';
import { AdvertisingTrafficService } from '../../core/service/advertising-traffic.service';
import { NMultisiteModal } from './multisite-modal.interfaces';

@Injectable({
    providedIn: 'root'
})
export class MultisiteModalInitiatorService extends AbstractDfModalInitiatorService<NMultisiteModal.IInitiatorData> implements OnDestroy {
    private subscription?: Subscription;
    private currentCallback?: (data: NMultisiteModal.IInitiatorData) => void;

    // Performance optimization: cache processed country sets
    private countryCache = new WeakMap<NMultisiteModal.IBlockContentVariant, Set<string>>();

    constructor(
        cmsModalService: ICmsModalService,
        protected override app: App,
        private multisiteService: MultisiteService,
        private iCmsBlockResource: ICmsBlockResource,
        private advertisingTrafficService: AdvertisingTrafficService
    ) {
        super(cmsModalService, app);
    }

    ngOnDestroy(): void {
        this.subscription?.unsubscribe();
    }

    listen(cb: (data: NMultisiteModal.IInitiatorData) => void): Promise<void> {
        return new Promise(resolve => {
            this.currentCallback = cb;
            this.checkMultisiteConditions();

            this.subscription = this.advertisingTrafficService.initialAdTrafficHandled$.subscribe(() => {
                this.checkMultisiteConditions(true);
            });

            resolve();
        });
    }

    /**
     * Gets cached country set for a store variant
     */
    private getCountrySet(store: NMultisiteModal.IBlockContentVariant): Set<string> {
        if (!this.countryCache.has(store)) {
            const countryArray: string[] = store.countries
                ? store.countries
                      .toUpperCase()
                      .split(',')
                      .map((c: string) => c.trim())
                : [];
            const countries = new Set<string>(countryArray);
            this.countryCache.set(store, countries);
        }
        return this.countryCache.get(store)!;
    }

    /**
     * Checks multisite conditions and triggers callback if modal should be shown
     */
    private checkMultisiteConditions(unsubscribe?: boolean): void {
        if (this.advertisingTrafficService.shouldSkipMultisiteForInitialAdTraffic()) {
            return;
        }

        if (!this.multisiteService.hasPreferredStore() && !this.multisiteService.noRedirect) {
            this.iCmsBlockResource
                .retrieve({ bareUrl: NMultisiteModal.componentId }, undefined)
                .then((block: ICmsBlock<NICmsBlock.IData<NMultisiteModal.IBlockContent>> | void) => {
                    if (block && this.currentCallback) {
                        this.app.customer.model
                            .load()
                            .then(a => {
                                const geo = a.data.geo;
                                if (geo) {
                                    // Use cached country sets for O(1) lookup
                                    const matchingStore = block.data?.content.variants.find((store: NMultisiteModal.IBlockContentVariant) =>
                                        this.getCountrySet(store).has(geo)
                                    );

                                    if (matchingStore) {
                                        if (unsubscribe) {
                                            this.subscription?.unsubscribe();
                                        }
                                        this.currentCallback!({ matchingCountry: geo });
                                    }
                                }
                            })
                            .catch(() => {});
                    }
                })
                .catch(() => {});
        }
    }
}
